import type { EmployeeRuntimeActor } from "../gateway/identity-client";
import type { TrustedTerminalIdentity } from "./terminal-state";
import { posRuntimeDb, type JournalRecord } from "./pos-db";
import { readLocalCart, type LocalCartDraft } from "./cart-state";

export type ParkedCartActor = {
  employeeId: string;
  employeeCode: string;
  employeeName: string;
};

export type LocalParkedCartDraft = {
  parkedCartId: string;
  cart: LocalCartDraft;
  parkedAt: string;
  parkedBy: ParkedCartActor;
};

function scopeKey(identity: TrustedTerminalIdentity) {
  return `org:${identity.organization_id}:branch:${identity.branch_id}:node:${identity.node_id}`;
}

function activeCartLocalId(identity: TrustedTerminalIdentity) {
  return `cart:${scopeKey(identity)}`;
}

function randomId(prefix: string) {
  const id =
    typeof crypto !== "undefined" && "randomUUID" in crypto
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
  return `${prefix}:${id}`;
}

function newCartRequestKey() {
  return randomId("pos-cart");
}

function parkedCartLocalId(identity: TrustedTerminalIdentity) {
  return `parked-cart:${scopeKey(identity)}:${randomId("draft")}`;
}

function actorSnapshot(actor: EmployeeRuntimeActor): ParkedCartActor {
  return {
    employeeId: actor.employee_id,
    employeeCode: actor.employee_code,
    employeeName: actor.employee_name,
  };
}

function emptyActiveCart(identity: TrustedTerminalIdentity, now: string): LocalCartDraft {
  return {
    localCartId: activeCartLocalId(identity),
    requestKey: newCartRequestKey(),
    lines: [],
    selectedMember: null,
    claimedPromotionVersionIds: [],
    quote: null,
    commerceCalculation: null,
    updatedAt: now,
  };
}

function activeCartRecord(
  identity: TrustedTerminalIdentity,
  cart: LocalCartDraft,
  now: string,
  createdAt: string,
): JournalRecord {
  const normalized = { ...cart, localCartId: activeCartLocalId(identity), updatedAt: now };
  return {
    localId: activeCartLocalId(identity),
    scopeKey: scopeKey(identity),
    kind: "CART_DRAFT",
    state: "OPEN",
    requestKey: normalized.requestKey,
    snapshot: normalized,
    createdAt,
    updatedAt: now,
  };
}

function parkedCartRecord(
  identity: TrustedTerminalIdentity,
  cart: LocalCartDraft,
  actor: EmployeeRuntimeActor,
  now: string,
): { record: JournalRecord; parked: LocalParkedCartDraft } {
  const parkedCartId = parkedCartLocalId(identity);
  const parked: LocalParkedCartDraft = {
    parkedCartId,
    cart: { ...cart, updatedAt: now },
    parkedAt: now,
    parkedBy: actorSnapshot(actor),
  };
  return {
    parked,
    record: {
      localId: parkedCartId,
      scopeKey: scopeKey(identity),
      kind: "PARKED_CART_DRAFT",
      state: "PARKED",
      requestKey: cart.requestKey,
      snapshot: parked,
      createdAt: now,
      updatedAt: now,
    },
  };
}

function parseParkedRecord(record: JournalRecord): LocalParkedCartDraft | null {
  if (record.kind !== "PARKED_CART_DRAFT" || record.state !== "PARKED") return null;
  if (!record.snapshot || typeof record.snapshot !== "object") return null;
  const snapshot = record.snapshot as Partial<LocalParkedCartDraft>;
  if (
    typeof snapshot.parkedCartId !== "string" ||
    !snapshot.parkedCartId ||
    typeof snapshot.parkedAt !== "string" ||
    !snapshot.cart ||
    typeof snapshot.cart !== "object" ||
    !snapshot.parkedBy ||
    typeof snapshot.parkedBy !== "object"
  ) {
    return null;
  }
  const cart = snapshot.cart as Partial<LocalCartDraft>;
  const parkedBy = snapshot.parkedBy as Partial<ParkedCartActor>;
  if (
    typeof cart.requestKey !== "string" ||
    !cart.requestKey ||
    !Array.isArray(cart.lines) ||
    typeof parkedBy.employeeId !== "string" ||
    typeof parkedBy.employeeCode !== "string" ||
    typeof parkedBy.employeeName !== "string"
  ) {
    return null;
  }
  return snapshot as LocalParkedCartDraft;
}

async function readParkedRecord(identity: TrustedTerminalIdentity, parkedCartId: string) {
  const record = await posRuntimeDb.journal.get(parkedCartId);
  if (!record || record.scopeKey !== scopeKey(identity)) return null;
  return parseParkedRecord(record);
}

export async function listLocalParkedCarts(identity: TrustedTerminalIdentity) {
  const records = await posRuntimeDb.journal.where("scopeKey").equals(scopeKey(identity)).toArray();
  return records
    .map(parseParkedRecord)
    .filter((value): value is LocalParkedCartDraft => Boolean(value))
    .sort((a, b) => a.parkedAt.localeCompare(b.parkedAt));
}

export async function countLocalParkedCarts(identity: TrustedTerminalIdentity) {
  const carts = await listLocalParkedCarts(identity);
  return carts.length;
}

export async function parkCurrentLocalCart(
  identity: TrustedTerminalIdentity,
  actor: EmployeeRuntimeActor,
) {
  return posRuntimeDb.transaction("rw", posRuntimeDb.journal, async () => {
    const current = await readLocalCart(identity);
    if (!current.lines.length) throw new Error("ACTIVE_CART_EMPTY");

    const now = new Date().toISOString();
    const activeRecord = await posRuntimeDb.journal.get(activeCartLocalId(identity));
    const { record, parked } = parkedCartRecord(identity, current, actor, now);
    await posRuntimeDb.journal.put(record);
    await posRuntimeDb.journal.put(
      activeCartRecord(
        identity,
        emptyActiveCart(identity, now),
        now,
        activeRecord?.createdAt ?? now,
      ),
    );
    return parked;
  });
}

export async function resumeLocalParkedCart(
  identity: TrustedTerminalIdentity,
  parkedCartId: string,
) {
  return posRuntimeDb.transaction("rw", posRuntimeDb.journal, async () => {
    const current = await readLocalCart(identity);
    if (current.lines.length) throw new Error("ACTIVE_CART_NOT_EMPTY");

    const parked = await readParkedRecord(identity, parkedCartId);
    if (!parked) throw new Error("PARKED_CART_NOT_FOUND");

    const now = new Date().toISOString();
    const activeRecord = await posRuntimeDb.journal.get(activeCartLocalId(identity));
    const resumed: LocalCartDraft = {
      ...parked.cart,
      localCartId: activeCartLocalId(identity),
      updatedAt: now,
    };
    await posRuntimeDb.journal.put(
      activeCartRecord(identity, resumed, now, activeRecord?.createdAt ?? now),
    );
    await posRuntimeDb.journal.delete(parkedCartId);
    return resumed;
  });
}

export async function parkCurrentAndResumeLocalCart(
  identity: TrustedTerminalIdentity,
  actor: EmployeeRuntimeActor,
  parkedCartId: string,
) {
  return posRuntimeDb.transaction("rw", posRuntimeDb.journal, async () => {
    const current = await readLocalCart(identity);
    const target = await readParkedRecord(identity, parkedCartId);
    if (!target) throw new Error("PARKED_CART_NOT_FOUND");

    const now = new Date().toISOString();
    const activeRecord = await posRuntimeDb.journal.get(activeCartLocalId(identity));
    const resumed: LocalCartDraft = {
      ...target.cart,
      localCartId: activeCartLocalId(identity),
      updatedAt: now,
    };

    if (current.lines.length) {
      const { record: parkedCurrent } = parkedCartRecord(identity, current, actor, now);
      await posRuntimeDb.journal.put(parkedCurrent);
    }

    await posRuntimeDb.journal.put(
      activeCartRecord(identity, resumed, now, activeRecord?.createdAt ?? now),
    );
    await posRuntimeDb.journal.delete(parkedCartId);
    return resumed;
  });
}

export async function discardLocalParkedCart(
  identity: TrustedTerminalIdentity,
  parkedCartId: string,
) {
  return posRuntimeDb.transaction("rw", posRuntimeDb.journal, async () => {
    const parked = await readParkedRecord(identity, parkedCartId);
    if (!parked) throw new Error("PARKED_CART_NOT_FOUND");
    await posRuntimeDb.journal.delete(parkedCartId);
  });
}
